import { useEffect, useRef, useState } from 'react';
import { api, type AssistantAnswer, type Affordability } from '../api.ts';
import { useApp, useFetch, useMoney } from '../store.tsx';
import { Card, Field, ProvenanceBadge, Sheet, Spinner } from '../components.tsx';
import { formatDate, parseAmountInput } from '../format.ts';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: { name: string }[];
  evidence?: AssistantAnswer['supportingTransactions'];
  usedFallback?: boolean;
}

/**
 * The assistant screen.
 *
 * Two things matter here beyond the chat itself: the answer must be able to
 * show the transactions behind it, and the user must be told when they are
 * talking to pattern matching rather than a model.
 */
export function AssistantScreen(): JSX.Element {
  const { t, locale, user, toastError } = useApp();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [showAfford, setShowAfford] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const { data: status } = useFetch<{ aiEnabled: boolean; mode: string; note: string }>('/assistant/status');
  const { data: suggestions } = useFetch<{ suggestions: string[] }>('/assistant/suggestions');

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, busy]);

  const ask = async (question: string) => {
    if (!question.trim() || busy) return;
    setTurns((prev) => [...prev, { role: 'user', content: question }]);
    setInput('');
    setBusy(true);
    try {
      const res = await api.post<AssistantAnswer>('/assistant/ask', { question, conversationId });
      setConversationId(res.conversationId);
      setTurns((prev) => [...prev, {
        role: 'assistant',
        content: res.answer,
        toolCalls: res.toolCalls,
        evidence: res.supportingTransactions,
        usedFallback: res.usedFallback,
      }]);
    } catch (err) {
      toastError(err);
      setTurns((prev) => [...prev, { role: 'assistant', content: t('somethingWrong') }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen screen--fill">
      <div className="row row--between" style={{ marginBottom: 10 }}>
        <h1 style={{ fontSize: 21, fontWeight: 680 }}>{t('assistant')}</h1>
        <button className="chip" onClick={() => setShowAfford(true)}>{t('canIAfford')}</button>
      </div>

      {status && !status.aiEnabled && (
        <Card style={{ marginBottom: 12, background: 'var(--bg-sunken)' }}>
          <div className="tiny muted" style={{ lineHeight: 1.6 }}>{t('deterministicMode')}</div>
        </Card>
      )}

      <div className="grow">
        {turns.length === 0 ? (
          <div>
            <p className="muted small" style={{ marginBottom: 16, lineHeight: 1.6 }}>{t('assistantIntro')}</p>
            <div className="stack stack--tight">
              {suggestions?.suggestions.map((s) => (
                <button key={s} className="card card--interactive small"
                        style={{ textAlign: 'start', padding: '13px 15px' }}
                        onClick={() => ask(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="chat">
            {turns.map((turn, i) => (
              <TurnBubble key={i} turn={turn} />
            ))}
            {busy && (
              <div className="bubble bubble--assistant"><Spinner label={t('thinking')} /></div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <form className="composer" onSubmit={(e) => { e.preventDefault(); ask(input); }}>
        <input
          className="input grow"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('askPlaceholder')}
          aria-label={t('askPlaceholder')}
          disabled={busy}
          dir={locale === 'ar' ? 'rtl' : 'ltr'}
        />
        <button className="btn btn--primary" type="submit" disabled={busy || !input.trim()}
                aria-label={t('assistant')} style={{ paddingInline: 16 }}>
          <span className="flip" aria-hidden>↑</span>
        </button>
      </form>

      {showAfford && <AffordabilitySheet onClose={() => setShowAfford(false)} currency={user?.currency ?? 'AED'} />}
    </div>
  );
}

function TurnBubble({ turn }: { turn: Turn }): JSX.Element {
  const { t, locale, user } = useApp();
  const money = useMoney();
  const [showEvidence, setShowEvidence] = useState(false);
  const currency = user?.currency ?? 'AED';

  if (turn.role === 'user') {
    return <div className="bubble bubble--user">{turn.content}</div>;
  }

  return (
    <div style={{ alignSelf: 'flex-start', maxWidth: '92%' }}>
      <div className="bubble bubble--assistant">{turn.content}</div>
      {turn.evidence && turn.evidence.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <button className="tiny" style={{ color: 'var(--accent)', background: 'none', paddingInline: 4 }}
                  onClick={() => setShowEvidence(!showEvidence)}>
            {showEvidence ? t('hideEvidence') : `${t('showEvidence')} (${turn.evidence.length})`}
          </button>
          {showEvidence && (
            <Card className="card--flush" style={{ marginTop: 6 }}>
              {turn.evidence.slice(0, 12).map((e) => (
                <div key={e.id} className="row row--between" style={{ padding: '9px 13px', borderBottom: '1px solid var(--border)' }}>
                  <span className="tiny truncate grow">
                    {e.merchantRaw ?? e.merchantNormalized ?? '—'}
                    <span className="muted"> · {formatDate(e.occurredAt, locale)}</span>
                  </span>
                  <span className="tiny strong money">{money(e.amountMinor, e.currency ?? currency)}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ── Can I afford it ──────────────────────────────────────────────────────────

function AffordabilitySheet({ onClose, currency }: { onClose: () => void; currency: string }): JSX.Element {
  const { t, locale } = useApp();
  const ar = locale === 'ar';
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Affordability | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = async () => {
    const minor = parseAmountInput(amount, currency);
    if (!minor) { setError(locale === 'ar' ? 'أدخل مبلغاً صحيحاً' : 'Enter a valid amount'); return; }
    setBusy(true);
    setError(null);
    try {
      setResult(await api.post<Affordability>('/affordability', { amountMinor: minor }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verdictText: Record<string, string> = {
    likely_affordable: t('likelyAffordable'),
    tight: t('tight'),
    likely_unaffordable: t('likelyUnaffordable'),
    insufficient_data: t('insufficientData'),
  };
  const verdictColor: Record<string, string> = {
    likely_affordable: 'var(--positive)',
    tight: 'var(--warning)',
    likely_unaffordable: 'var(--negative)',
    insufficient_data: 'var(--text-secondary)',
  };

  return (
    <Sheet title={t('canIAfford')} onClose={onClose}>
      <div className="stack">
        <Field label={`${t('affordPrompt')} (${currency})`} error={error}>
          <input className="input" inputMode="decimal" autoFocus value={amount}
                 onChange={(e) => { setAmount(e.target.value); setResult(null); }} placeholder="0.00" />
        </Field>
        <button className="btn btn--primary btn--block" disabled={busy} onClick={check}>
          {busy ? t('loading') : t('checkIt')}
        </button>

        {result && (
          <>
            <Card style={{ borderColor: verdictColor[result.verdict] }}>
              <div style={{ fontSize: 17, fontWeight: 660, color: verdictColor[result.verdict] }}>
                {verdictText[result.verdict]}
              </div>
              {result.verdict !== 'insufficient_data' && (
                <div className="stack stack--tight" style={{ marginTop: 14 }}>
                  <Figure label={t('projectedSurplus')} value={result.figures.projectedSurplusMinor} currency={currency} kind="estimated" />
                  <Figure label={t('afterPurchase')} value={result.figures.surplusAfterPurchaseMinor} currency={currency} kind="estimated" />
                  <Figure label={t('accountBalance')} value={result.figures.knownBalanceMinor} currency={currency} kind="confirmed" />
                </div>
              )}
            </Card>

            {result.assumptions.length > 0 && (
              <Card>
                <div className="field__label" style={{ marginBottom: 8 }}>{t('assumptions')}</div>
                <ul className="small muted" style={{ lineHeight: 1.65 }}>
                  {(ar && result.assumptionsAr?.length ? result.assumptionsAr : result.assumptions)
                    .map((a) => <li key={a}>· {a}</li>)}
                </ul>
              </Card>
            )}

            {result.limitations.length > 0 && (
              <Card style={{ borderColor: 'var(--warning)' }}>
                <div className="field__label" style={{ marginBottom: 8 }}>{t('limitations')}</div>
                <ul className="small muted" style={{ lineHeight: 1.65 }}>
                  {(ar && result.limitationsAr?.length ? result.limitationsAr : result.limitations)
                    .map((l) => <li key={l}>· {l}</li>)}
                </ul>
              </Card>
            )}

            <p className="tiny muted center" style={{ lineHeight: 1.6 }}>{t('notAdvice')}</p>
          </>
        )}
      </div>
    </Sheet>
  );
}

function Figure({ label, value, currency, kind }: {
  label: string; value: number | null; currency: string; kind: 'confirmed' | 'estimated';
}): JSX.Element {
  const { t } = useApp();
  const money = useMoney();
  return (
    <div className="row row--between" style={{ gap: 12 }}>
      <span className="small muted row" style={{ gap: 6 }}>
        {label}
        {value != null && <ProvenanceBadge kind={kind} />}
      </span>
      <span className="small strong money" style={{
        color: value != null && value < 0 ? 'var(--negative)' : undefined,
      }}>
        {value == null ? t('notAvailable') : money(value, currency)}
      </span>
    </div>
  );
}
